/** Restore points — continuous workspace snapshots (Step 8). */
import { accountBundle, emitAccountsChange } from "./store.js";
import { getLifecycle } from "../api/accountLifecycle.js";
import { canManageAccounts } from "./constants.js";
import { listTable, upsertRow, resetJourneyDbCache, TABLES } from "../db/index.js";

export const RESTORE_EVENT = "dh-restore-change";
export const MAX_POINTS = 400;
export const MAX_PER_USER = 120;

export const RESTORE_SCOPES = {
  account: "account",
  buyer: "buyer",
  seller: "seller",
  agent_desk: "agent_desk",
  broker_desk: "broker_desk",
  settings: "settings",
  messaging: "messaging",
};

export const SCOPE_LABELS = {
  account: "Account & billing",
  buyer: "Buyer checklist",
  seller: "Seller portfolio",
  agent_desk: "Agent desk",
  broker_desk: "Broker desk",
  settings: "Account settings",
  messaging: "Messages & threads",
};

const KEY = "dh_restore_points";

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function writeAll(rows) {
  localStorage.setItem(KEY, JSON.stringify(rows.slice(0, MAX_POINTS)));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(RESTORE_EVENT, { detail: { at: Date.now() } }));
  }
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function simpleHash(value) {
  const str = typeof value === "string" ? value : JSON.stringify(value);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
}

function buyerRow(userId) {
  return readJson("dh_buyers", []).find((row) => row.id === userId) || null;
}

function sellerRow(userId) {
  const current = readJson("dh_seller", null);
  if (current?.id === userId) return current;
  return readJson("dh_sellers", []).find((row) => row.id === userId) || null;
}

function captureMessagingSlice(userId) {
  if (!userId) return null;
  const transactions = listTable(TABLES.transactions).filter(
    (row) => row.agent_id === userId || row.broker_id === userId || row.client_user_id === userId
  );
  const txIds = new Set(transactions.map((row) => row.id));
  const threads = listTable(TABLES.message_threads).filter((row) => txIds.has(row.transaction_id));
  const threadIds = new Set(threads.map((row) => row.id));
  const messages = listTable(TABLES.messages).filter((row) => threadIds.has(row.thread_id));
  const messageIds = new Set(messages.map((row) => row.id));
  const attachments = listTable(TABLES.attachments).filter((row) => messageIds.has(row.message_id));
  const notifications = listTable(TABLES.notifications).filter((row) => row.user_id === userId);
  return { transactions, threads, messages, attachments, notifications };
}

export function captureWorkspaceSnapshot(userId, scope) {
  if (!userId) return null;
  switch (scope) {
    case RESTORE_SCOPES.buyer: {
      const buyer = buyerRow(userId);
      const current = readJson("dh_buyer_current", null);
      return {
        buyer,
        buyerCurrent: current?.id === userId ? current : null,
      };
    }
    case RESTORE_SCOPES.seller: {
      const seller = sellerRow(userId);
      const list = readJson("dh_sellers", []).filter((row) => row.id === userId);
      const current = readJson("dh_seller", null);
      return {
        seller,
        sellersList: list,
        sellerCurrent: current?.id === userId ? current : null,
      };
    }
    case RESTORE_SCOPES.agent_desk:
      return {
        profile: readJson("dh_agent_profiles", {})[userId] || null,
        agent: readJson("dh_agents", []).find((row) => row.id === userId) || null,
        settings: readJson("dh_account_settings", {})[userId] || null,
        lifecycle: getLifecycle(userId),
      };
    case RESTORE_SCOPES.broker_desk:
      return {
        profile: readJson("dh_broker_profiles", {})[userId] || null,
        agent: readJson("dh_agents", []).find((row) => row.id === userId) || null,
        settings: readJson("dh_account_settings", {})[userId] || null,
        lifecycle: getLifecycle(userId),
      };
    case RESTORE_SCOPES.settings:
      return { settings: readJson("dh_account_settings", {})[userId] || null };
    case RESTORE_SCOPES.messaging:
      return captureMessagingSlice(userId);
    case RESTORE_SCOPES.account:
    default: {
      const bundle = accountBundle(userId);
      return {
        account: bundle?.account || null,
        subscription: bundle?.subscription || null,
        billing: bundle?.billing || null,
        lifecycle: getLifecycle(bundle?.account?.journey_user_id || userId),
      };
    }
  }
}

function normalizeScope(scope, userId, accountId) {
  if (scope && RESTORE_SCOPES[scope]) return scope;
  if (accountId || scope === RESTORE_SCOPES.account) return RESTORE_SCOPES.account;
  const buyer = buyerRow(userId);
  if (buyer) return RESTORE_SCOPES.buyer;
  const seller = sellerRow(userId);
  if (seller) return RESTORE_SCOPES.seller;
  return RESTORE_SCOPES.account;
}

function lastPointFor(userId, scope, actionType) {
  return readAll().find(
    (row) =>
      row.userId === userId &&
      (row.scope || RESTORE_SCOPES.account) === scope &&
      (row.actionType || "") === (actionType || "")
  );
}

export function captureRestorePoint(
  userId,
  {
    label = "Snapshot",
    actorId = "",
    accountId = "",
    scope = "",
    source = "manual",
    actionType = "",
    force = false,
  } = {}
) {
  if (!userId && !accountId) return null;
  const ownerId = userId || accountId;
  const accountOwnerId = accountId || userId;
  const resolvedScope = normalizeScope(scope, ownerId, accountId);
  const payload =
    resolvedScope === RESTORE_SCOPES.account
      ? captureWorkspaceSnapshot(accountOwnerId, resolvedScope)
      : captureWorkspaceSnapshot(ownerId, resolvedScope);
  if (!payload) return null;
  if (resolvedScope === RESTORE_SCOPES.account && !payload.account) return null;
  if (
    resolvedScope !== RESTORE_SCOPES.account &&
    !payload.buyer &&
    !payload.seller &&
    !payload.profile &&
    !payload.settings &&
    !payload.transactions
  ) {
    return null;
  }
  const fingerprint = simpleHash(payload);
  if (!force && source === "auto") {
    const prev = lastPointFor(ownerId, resolvedScope, actionType);
    if (prev?.fingerprint === fingerprint) return prev;
  }
  const row = {
    id: crypto.randomUUID(),
    userId: ownerId,
    accountId: accountId || "",
    scope: resolvedScope,
    label: String(label || "Snapshot").trim(),
    source: source || "manual",
    actionType: actionType || "",
    actorId: actorId || "",
    at: new Date().toISOString(),
    fingerprint,
    payload,
  };
  const rows = readAll();
  const kept = rows.filter((item) => item.userId !== ownerId).concat(rows.filter((item) => item.userId === ownerId).slice(0, MAX_PER_USER - 1));
  writeAll([row, ...kept]);
  return row;
}

export function listRestorePoints(userId, { scope = "", limit = 40 } = {}) {
  let rows = readAll();
  if (userId) {
    rows = rows.filter((row) => row.userId === userId || row.accountId === userId);
  }
  if (scope) rows = rows.filter((row) => (row.scope || RESTORE_SCOPES.account) === scope);
  return rows.slice(0, limit);
}

export function listRestoreTimeline(userId, { scope = "", limit = 60 } = {}) {
  return listRestorePoints(userId, { scope, limit }).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function getRestorePoint(pointId) {
  return readAll().find((row) => row.id === pointId) || null;
}

export function scopeLabel(scope) {
  return SCOPE_LABELS[scope] || SCOPE_LABELS.account;
}

export function canRestorePoint(session, point) {
  if (!session || !point) return false;
  if (canManageAccounts(session)) return true;
  return session.userId === point.userId;
}

export function describeRestorePoint(point) {
  if (!point) return "";
  const scope = scopeLabel(point.scope || RESTORE_SCOPES.account);
  const when = new Date(point.at).toLocaleString();
  return `${scope} · ${point.label || "Snapshot"} · ${when}`;
}

function applyBuyerPayload(userId, payload) {
  if (!payload?.buyer) return;
  const buyers = readJson("dh_buyers", []);
  const index = buyers.findIndex((row) => row.id === userId);
  if (index >= 0) buyers[index] = { ...buyers[index], ...payload.buyer };
  else buyers.push(payload.buyer);
  localStorage.setItem("dh_buyers", JSON.stringify(buyers));
  if (payload.buyerCurrent) localStorage.setItem("dh_buyer_current", JSON.stringify(payload.buyerCurrent));
}

function applySellerPayload(userId, payload) {
  if (!payload?.seller) return;
  const list = readJson("dh_sellers", []);
  const index = list.findIndex((row) => row.id === userId);
  if (index >= 0) list[index] = { ...list[index], ...payload.seller };
  else list.push(payload.seller);
  localStorage.setItem("dh_sellers", JSON.stringify(list));
  if (payload.sellerCurrent) localStorage.setItem("dh_seller", JSON.stringify(payload.sellerCurrent));
  else localStorage.setItem("dh_seller", JSON.stringify(payload.seller));
}

function applySettingsPayload(userId, payload) {
  if (!payload?.settings) return;
  const all = readJson("dh_account_settings", {});
  all[userId] = payload.settings;
  localStorage.setItem("dh_account_settings", JSON.stringify(all));
}

function applyAgentDeskPayload(userId, payload) {
  if (payload?.profile) {
    const all = readJson("dh_agent_profiles", {});
    all[userId] = payload.profile;
    localStorage.setItem("dh_agent_profiles", JSON.stringify(all));
  }
  if (payload?.agent) {
    const agents = readJson("dh_agents", []);
    const index = agents.findIndex((row) => row.id === userId);
    if (index >= 0) agents[index] = { ...agents[index], ...payload.agent };
    localStorage.setItem("dh_agents", JSON.stringify(agents));
  }
  if (payload?.settings) applySettingsPayload(userId, payload);
  if (payload?.lifecycle) {
    const map = readJson("dh_account_lifecycle", {});
    map[userId] = payload.lifecycle;
    localStorage.setItem("dh_account_lifecycle", JSON.stringify(map));
  }
}

function applyBrokerDeskPayload(userId, payload) {
  if (payload?.profile) {
    const all = readJson("dh_broker_profiles", {});
    all[userId] = payload.profile;
    localStorage.setItem("dh_broker_profiles", JSON.stringify(all));
  }
  applyAgentDeskPayload(userId, payload);
}

function applyMessagingPayload(payload) {
  if (!payload) return;
  const tables = [
    [TABLES.transactions, payload.transactions],
    [TABLES.message_threads, payload.threads],
    [TABLES.messages, payload.messages],
    [TABLES.attachments, payload.attachments],
    [TABLES.notifications, payload.notifications],
  ];
  tables.forEach(([table, rows]) => {
    (rows || []).forEach((row) => {
      if (row?.id) upsertRow(table, row);
    });
  });
  resetJourneyDbCache();
}

function applyAccountPayload(payload) {
  const { account, subscription, billing, lifecycle } = payload || {};
  if (!account) throw new Error("Restore point not found.");
  const accounts = readJson("dh_accounts_users", []);
  const index = accounts.findIndex((row) => row.id === account.id);
  if (index === -1) throw new Error("Account no longer exists on this device.");
  accounts[index] = {
    ...accounts[index],
    ...account,
    status: account.status === "deleted" ? "active" : account.status,
  };
  localStorage.setItem("dh_accounts_users", JSON.stringify(accounts));
  if (subscription) {
    const subs = readJson("dh_accounts_subs", []);
    const subIdx = subs.findIndex((row) => row.id === subscription.id);
    if (subIdx >= 0) subs[subIdx] = { ...subs[subIdx], ...subscription };
    else subs.push(subscription);
    localStorage.setItem("dh_accounts_subs", JSON.stringify(subs));
  }
  if (billing) {
    const profiles = readJson("dh_accounts_billing", []);
    const billIdx = profiles.findIndex((row) => row.owner_id === billing.owner_id);
    if (billIdx >= 0) profiles[billIdx] = { ...profiles[billIdx], ...billing };
    else profiles.push(billing);
    localStorage.setItem("dh_accounts_billing", JSON.stringify(profiles));
  }
  if (lifecycle) {
    const map = readJson("dh_account_lifecycle", {});
    map[account.journey_user_id || account.id] = lifecycle;
    localStorage.setItem("dh_account_lifecycle", JSON.stringify(map));
  }
  emitAccountsChange();
}

export function restoreFromPoint(session, pointId, { confirmed = false } = {}) {
  if (!confirmed) throw new Error("Restore confirmation required.");
  const point = getRestorePoint(pointId);
  if (!point) throw new Error("Restore point not found.");
  if (!canRestorePoint(session, point)) throw new Error("You can only restore your own snapshots.");
  const scope = point.scope || RESTORE_SCOPES.account;
  const userId = point.userId;
  switch (scope) {
    case RESTORE_SCOPES.buyer:
      applyBuyerPayload(userId, point.payload);
      break;
    case RESTORE_SCOPES.seller:
      applySellerPayload(userId, point.payload);
      break;
    case RESTORE_SCOPES.settings:
      applySettingsPayload(userId, point.payload);
      break;
    case RESTORE_SCOPES.agent_desk:
      applyAgentDeskPayload(userId, point.payload);
      break;
    case RESTORE_SCOPES.broker_desk:
      applyBrokerDeskPayload(userId, point.payload);
      break;
    case RESTORE_SCOPES.messaging:
      applyMessagingPayload(point.payload);
      break;
    case RESTORE_SCOPES.account:
    default:
      applyAccountPayload(point.payload);
      break;
  }
  captureRestorePoint(userId, {
    label: `Before restore · ${point.label || scopeLabel(scope)}`,
    actorId: session?.userId || "",
    accountId: point.accountId || "",
    scope,
    source: "pre_change",
    force: true,
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(RESTORE_EVENT, { detail: { restored: pointId, at: Date.now() } }));
  }
  return point;
}
